import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, Location } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AuthService } from '@app/services/auth.service';
import { DiagnosisParams } from '@app/services/message.service';
import { PaymentService } from '@app/services/payment.service';
import { TabService } from '@app/services/tab.service';
import { ModalService } from '@app/components/modal/modal.service';
import { WelcomeScreenComponent } from '@app/components/welcome-screen/welcome-screen.component';
import { ArticleComponent } from '@app/article/article.component';
import { ChatTabComponent } from '@app/chat/chat-tab/chat-tab.component';
import { HpDiagnosisComponent } from '../components/hp-diagnosis/hp-diagnosis.component';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-chat-tabs',
  standalone: true,

  imports: [CommonModule, TranslocoPipe, ChatTabComponent, WelcomeScreenComponent],

  templateUrl: './chat-tabs.component.html',
  styleUrl: './chat-tabs.component.scss',
})
export class ChatTabsComponent implements OnInit {
  tabService = inject(TabService);
  route = inject(ActivatedRoute);
  paymentService = inject(PaymentService);
  modalService = inject(ModalService);
  location = inject(Location);
  router = inject(Router);
  authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  openendArticle: string | undefined;
  openedArticleDialogRef: string | undefined;
  diagnosisModelRef: string | undefined;

  activeTab = computed(() => this.tabService.activeTab());
  tabs = computed(() => this.tabService.tabs());
  subscriptionExpired = computed(() => this.paymentService.subscriptionExpired());

  ngOnInit() {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (this.subscriptionExpired()) {
        console.warn('[ChatTabsComponent] No valid user subscription. Cannot open views.');
        return;
      }

      // open article view
      if (Object.keys(params).includes('articleView')) {
        this._openArticleView(params);
      } else {
        this._closeArticleView();
      }

      // open diagnosis view
      if (Object.keys(params).includes('diagnosisView')) {
        this._openDiagnosisView(params as DiagnosisParams);
      } else {
        this._closeDiagnosisView();
      }
    });
  }

  private _openArticleView(params: Params) {
    const dialogKey = JSON.stringify(params);

    this._closeArticleView();

    if (this.openendArticle === dialogKey) return;
    this.openendArticle = dialogKey;

    this.openedArticleDialogRef = this.modalService.open(ArticleComponent, params, {
      screen: 'full',
      showPrintButton: true,
      closeOnEscape: false,
      onClose: () => this._clearUrlParams(), // this.location.back(),
    });
  }

  private _closeArticleView() {
    // Close without callbacks: same reason as _closeDiagnosisView — URL already changed.
    this.modalService.close(this.openedArticleDialogRef, false);
    this.openendArticle = undefined;
  }

  private _openDiagnosisView(params: DiagnosisParams): void {
    if (this.diagnosisModelRef) {
      return; // already open
    }

    this.diagnosisModelRef = this.modalService.open(
      HpDiagnosisComponent,
      {
        carTypeId: params.carTypeId,
        errorCode: params.errorCode,
        componentIds: params.componentIds,
        selectedVariantId: params.variantId,
        selectedVariantGroupId: params.variantGroupId,
        selectedComponentId: params.componentId,
      },
      {
        screen: 'full',
        showCloseButton: false,
        onClose: () => this._clearUrlParams(),
      },
    );
  }

  private _closeDiagnosisView(): void {
    // Close without callbacks: the URL has already changed away from diagnosisView,
    // so calling onClose → _clearUrlParams() would be a redundant navigation that
    // corrupts the history and displays url1 instead of the intended page.
    this.modalService.close(this.diagnosisModelRef, false);
    this.diagnosisModelRef = undefined;
  }

  private _clearUrlParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      queryParamsHandling: '',
    });
  }
}
